import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, MessageCircle } from "lucide-react";
import MarketingFooter from "../components/MarketingFooter";
import MarketingPageShell from "../components/MarketingPageShell";
import usePageMeta from "../hooks/usePageMeta";

const CHECKLIST = [
  {
    t: "M-Pesa transaction ID",
    d: "The 10-character reference from the M-Pesa confirmation SMS (e.g. QXY12AB3CD).",
  },
  {
    t: "Screenshot evidence",
    d: "M-Pesa confirmation SMS, suspicious customer message, fraudulent reversal request, etc.",
  },
  {
    t: "Customer conversation",
    d: "WhatsApp / SMS / email exchange showing the order, the dispute, or the fraud attempt.",
  },
  {
    t: "Delivery proof (if applicable)",
    d: "Rider receipt, delivery confirmation, dispatch photo — anything proving goods left your premises.",
  },
  {
    t: "Date and amount lost",
    d: "When it happened and the KES value — even a rough estimate helps speed up triage.",
  },
];

export default function ClaimsHelp() {
  usePageMeta({
    title: "Claims help — Fortiqa",
    description: "Everything you need to submit a Fortiqa claim — evidence checklist, what we accept, and how the review works.",
  });
  return (
    <div className="min-h-screen bg-[#f4f2eb] text-[#1b1d1c]" data-testid="claims-help-page">
      <MarketingPageShell
        eyebrow="Claims help"
        title="Submit a claim in under 2 minutes."
        intro="The faster we get the right evidence, the faster you get paid. Here's exactly what to send — and how FlashClaim reviews each piece."
        testid="claims-help"
      >
        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-12">
          <div className="rounded-3xl bg-white border border-[#114232]/10 p-7 sm:p-10">
            <div className="font-heading text-xl sm:text-2xl font-bold text-[#114232] mb-6">
              Evidence checklist
            </div>
            <ul className="space-y-4">
              {CHECKLIST.map(({ t, d }) => (
                <li key={t} className="flex items-start gap-3.5">
                  <CheckCircle2 className="w-5 h-5 text-[#2d7a4a] mt-0.5 shrink-0" strokeWidth={2} />
                  <div>
                    <div className="font-heading font-bold text-[#114232]">{t}</div>
                    <p className="text-sm text-[#5c6360] leading-relaxed mt-0.5">{d}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-12 grid sm:grid-cols-3 gap-4">
          {[
            { t: "What we accept", d: "PNG, JPG, PDF up to 10MB per file. Up to 6 files per claim." },
            { t: "Decision speed", d: "Clean claims auto-decide in < 90s. Edge cases route to SIU within minutes." },
            { t: "Payout method", d: "M-Pesa B2C transfer back to the same till that was defrauded." },
          ].map(({ t, d }) => (
            <div key={t} className="rounded-2xl bg-white border border-[#114232]/10 p-6">
              <div className="font-heading text-base font-bold text-[#114232] mb-1.5">{t}</div>
              <p className="text-sm text-[#5c6360] leading-relaxed">{d}</p>
            </div>
          ))}
        </section>

        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-24">
          <div className="bg-[#114232] rounded-3xl p-8 sm:p-10 lg:p-14 text-[#f4f2eb] flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#d49a36] mb-2">
                Got everything ready?
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl font-bold leading-tight">
                Submit your claim on WhatsApp now.
              </h3>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="https://wa.me/254700000000?text=Hi%20Fortiqa%2C%20I%20want%20to%20submit%20a%20claim"
                target="_blank"
                rel="noreferrer"
                data-testid="claims-help-cta-whatsapp"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#c15337] hover:bg-[#a8472f] text-white font-semibold transition-all shadow-md hover:shadow-lg"
              >
                <MessageCircle className="w-4 h-4" /> Submit on WhatsApp
              </a>
              <Link
                to="/flashclaim"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white/10 hover:bg-white/15 text-[#f4f2eb] font-semibold transition-all border border-white/20"
              >
                How FlashClaim works <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </MarketingPageShell>
      <MarketingFooter />
    </div>
  );
}
